import React from 'react';
import { EnterpriseAuditResult, Page, NavParams, RemediationAction } from '../../../functions/src/types';
import { Section, ICONS } from './ReportShared';
import { COUNTRIES } from '../../../services/locationData';

export interface EnterpriseAuditResultDisplayProps {
    result: EnterpriseAuditResult;
    onNavigate?: (nav: { page: Page, params?: NavParams }) => void;
}

const PMESII_LABELS: Record<string, { label: string; color: string }> = {
    political:      { label: 'Political',      color: 'bg-blue-50 border-blue-200 text-blue-800' },
    military:       { label: 'Military',       color: 'bg-red-50 border-red-200 text-red-800' },
    economic:       { label: 'Economic',       color: 'bg-amber-50 border-amber-200 text-amber-800' },
    social:         { label: 'Social',         color: 'bg-purple-50 border-purple-200 text-purple-800' },
    infrastructure: { label: 'Infrastructure', color: 'bg-slate-50 border-slate-200 text-slate-800' },
    information:    { label: 'Information',    color: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
    physical:       { label: 'Physical',       color: 'bg-orange-50 border-orange-200 text-orange-800' },
    time:           { label: 'Time',           color: 'bg-green-50 border-green-200 text-green-800' },
};

export const EnterpriseAuditResultDisplay: React.FC<EnterpriseAuditResultDisplayProps> = ({ result, onNavigate }) => {
    const r = result as any; // Allow access to extended fields not yet in the type
    const [pendingAction, setPendingAction] = React.useState<RemediationAction | null>(null);
    const [scope, setScope] = React.useState<'global' | 'country'>('global');
    const [targetCountry, setTargetCountry] = React.useState('');
    const [countryError, setCountryError] = React.useState('');

    const openRemediationContext = (action: RemediationAction) => {
        const existingCountry = String(action.params?.country || action.params?.preselectedCountry || '');
        setPendingAction(action);
        setScope(existingCountry ? 'country' : 'global');
        setTargetCountry(existingCountry);
        setCountryError('');
    };

    const executeRemediation = () => {
        if (!pendingAction || !onNavigate)
            return;
        if (scope === 'country' && !COUNTRIES.includes(targetCountry)) {
            setCountryError('Select a country or territory from the list.');
            return;
        }
        const routeMap: Record<string, { suiteName: string; workflowId: string }> = {
            'Crisis Management': {
                suiteName: 'Company Business Resiliency and Crises Management Program',
                workflowId: 'crisis_management_plan'
            },
            'Business Resiliency': {
                suiteName: 'Company Business Resiliency and Crises Management Program',
                workflowId: 'business_resiliency_framework'
            },
            'Emergency Preparedness': {
                suiteName: 'Company Business Resiliency and Crises Management Program',
                workflowId: 'emergency_preparedness_plan'
            },
            'HSSE Compliance': {
                suiteName: 'HSSE Compliance',
                workflowId: 'compliance_audit'
            },
            'SRM': {
                suiteName: 'Security Risk Management',
                workflowId: 'facility_risk_assessment'
            }
        };
        const route = routeMap[pendingAction.targetSuite] || {
            suiteName: pendingAction.targetSuite,
            workflowId: pendingAction.targetWorkflow
        };
        const country = scope === 'country' ? targetCountry : 'Global';
        const params = pendingAction.params || {};
        onNavigate({
            page: 'suiteDashboard',
            params: {
                ...params,
                suiteName: route.suiteName,
                workflow: route.workflowId,
                workflowId: route.workflowId,
                activeWorkflowId: route.workflowId,
                country,
                initialData: {
                    ...(params.initialData || {}),
                    country,
                    location_country: country,
                    remediationActionId: pendingAction.id,
                    remediationTitle: pendingAction.title,
                    remediationDescription: pendingAction.description
                }
            }
        });
        setPendingAction(null);
    };

    return (
        <div className="space-y-8 animate-fade-in">

            {/* ── OVERALL POSTURE BANNER ── */}
            <div className="bg-slate-900 border border-slate-700 p-6 rounded-xl text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10 text-red-500">
                    <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 22h20L12 2zm0 3.8l7.5 15H4.5L12 5.8zM11 10v5h2v-5h-2zm0 7v2h2v-2h-2z"/></svg>
                </div>
                <h3 className="text-xl font-black uppercase tracking-widest text-red-500 mb-2 relative z-10 flex items-center">
                    <span className="mr-3">{ICONS.activity}</span>
                    OVERALL POSTURE: {result.overallRiskPosture}
                </h3>
                {r.executiveSummary && (
                    <p className="text-slate-300 text-sm relative z-10 max-w-3xl mt-1">{r.executiveSummary}</p>
                )}
                {!r.executiveSummary && (
                    <p className="text-slate-400 text-sm relative z-10 max-w-2xl">This strategic audit synthesizes current geopolitical telemetry, physical asset footprints, and active threat proximity to project the most critical operational vectors.</p>
                )}
            </div>

            {/* ── MDCOA / MLCOA ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
                    <h4 className="flex items-center text-red-800 font-bold uppercase tracking-wider mb-4 border-b border-red-200 pb-2">
                        <span className="bg-red-100 text-red-600 p-1.5 rounded mr-3 text-xs">MDCOA</span>
                        Most Dangerous Course
                    </h4>
                    <h5 className="font-bold text-slate-800 mb-2">{result.mdcoa?.title || 'MDCOA'}</h5>
                    <p className="text-sm text-slate-600 mb-4">
                        {typeof result.mdcoa === 'string' ? result.mdcoa : result.mdcoa?.description || ''}
                    </p>
                    {result.mdcoa?.mitigation && (
                        <div className="bg-white p-3 rounded border border-red-100 text-sm">
                            <span className="font-bold text-red-700 block mb-1">Strategic Mitigation:</span>
                            {result.mdcoa.mitigation}
                        </div>
                    )}
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm">
                    <h4 className="flex items-center text-orange-800 font-bold uppercase tracking-wider mb-4 border-b border-orange-200 pb-2">
                        <span className="bg-orange-100 text-orange-600 p-1.5 rounded mr-3 text-xs">MLCOA</span>
                        Most Likely Course
                    </h4>
                    <h5 className="font-bold text-slate-800 mb-2">{result.mlcoa?.title || 'MLCOA'}</h5>
                    <p className="text-sm text-slate-600 mb-4">
                        {typeof result.mlcoa === 'string' ? result.mlcoa : result.mlcoa?.description || ''}
                    </p>
                    {result.mlcoa?.mitigation && (
                        <div className="bg-white p-3 rounded border border-orange-100 text-sm">
                            <span className="font-bold text-orange-700 block mb-1">Strategic Mitigation:</span>
                            {result.mlcoa.mitigation}
                        </div>
                    )}
                </div>
            </div>

            {/* ── BEST CASE ── */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 shadow-sm">
                <h4 className="flex items-center text-emerald-800 font-bold uppercase tracking-wider mb-4 border-b border-emerald-200 pb-2">
                    <span className="bg-emerald-100 text-emerald-600 p-1.5 rounded mr-3 text-xs">OPT</span>
                    Best Case Scenario / Opportunities
                </h4>
                <h5 className="font-bold text-slate-800 mb-2">{result.bestCase?.title || 'Best Case'}</h5>
                <p className="text-sm text-slate-600 min-h-[60px]">
                    {typeof result.bestCase === 'string' ? result.bestCase : result.bestCase?.description || ''}
                </p>
            </div>

            {/* ── PMESII-PT BREAKDOWN ── */}
            {r.pmesiiBreakdown && Object.keys(r.pmesiiBreakdown).length > 0 && (
                <Section title="PMESII-PT Domain Assessment">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(r.pmesiiBreakdown).map(([key, value]) => {
                            const meta = PMESII_LABELS[key] || { label: key, color: 'bg-slate-50 border-slate-200 text-slate-800' };
                            return (
                                <div key={key} className={`border rounded-xl p-4 ${meta.color.split(' ').slice(0,2).join(' ')}`}>
                                    <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${meta.color.split(' ')[2]}`}>
                                        {meta.label}
                                    </div>
                                    <p className="text-sm text-slate-700 leading-relaxed">{String(value)}</p>
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            {/* ── PER-ASSET RISK SCORING ── */}
            {r.assetRiskScores && r.assetRiskScores.length > 0 && (
                <Section title="Per-Asset Risk Scoring">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {r.assetRiskScores.map((asset: any, i: number) => {
                            const riskColor =
                                asset.riskLevel === 'Critical' ? 'border-red-300 bg-red-50' :
                                asset.riskLevel === 'High'     ? 'border-orange-300 bg-orange-50' :
                                asset.riskLevel === 'Medium'   ? 'border-amber-300 bg-amber-50' :
                                'border-emerald-300 bg-emerald-50';
                            const badgeColor =
                                asset.riskLevel === 'Critical' ? 'bg-red-600 text-white' :
                                asset.riskLevel === 'High'     ? 'bg-orange-600 text-white' :
                                asset.riskLevel === 'Medium'   ? 'bg-amber-500 text-white' :
                                'bg-emerald-600 text-white';
                            return (
                                <div key={i} className={`border rounded-xl p-5 ${riskColor}`}>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <p className="font-black text-slate-900 uppercase tracking-tight text-sm">{asset.assetName}</p>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{asset.assetType} · {asset.country}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest ${badgeColor}`}>
                                                {asset.riskLevel}
                                            </span>
                                            {asset.riskScore != null && (
                                                <span className="text-[10px] text-slate-500 font-bold">Score: {asset.riskScore}/100</span>
                                            )}
                                        </div>
                                    </div>
                                    {asset.primaryThreatVector && (
                                        <div className="mb-2">
                                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Primary Threat Vector</span>
                                            <p className="text-xs text-slate-700 font-semibold">{asset.primaryThreatVector}</p>
                                        </div>
                                    )}
                                    {asset.mlcoaExposure && (
                                        <div className="mb-1">
                                            <span className="text-[9px] font-black text-orange-600 uppercase tracking-widest">MLCOA Exposure</span>
                                            <p className="text-xs text-slate-600">{asset.mlcoaExposure}</p>
                                        </div>
                                    )}
                                    {asset.mdcoaExposure && (
                                        <div className="mb-1">
                                            <span className="text-[9px] font-black text-red-600 uppercase tracking-widest">MDCOA Exposure</span>
                                            <p className="text-xs text-slate-600">{asset.mdcoaExposure}</p>
                                        </div>
                                    )}
                                    {asset.recommendedControl && (
                                        <div className="mt-3 bg-white/70 rounded-lg p-3 border border-white">
                                            <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest">Recommended Control</span>
                                            <p className="text-xs text-slate-700 mt-0.5">{asset.recommendedControl}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            {/* ── PERSONNEL RISK ── */}
            {r.personnelRisk && (
                <Section title="Personnel & Duty-of-Care Risk">
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                        <p className="text-sm text-slate-700 mb-4">{r.personnelRisk.summary}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {r.personnelRisk.highRiskLocations?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-purple-700 mb-2">High-Risk Locations</h6>
                                    <ul className="space-y-1">
                                        {r.personnelRisk.highRiskLocations.map((loc: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-purple-500 mt-0.5">⚑</span>{loc}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {r.personnelRisk.recommendedActions?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-purple-700 mb-2">Recommended Actions</h6>
                                    <ul className="space-y-1">
                                        {r.personnelRisk.recommendedActions.map((action: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-emerald-500 mt-0.5">✓</span>{action}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            )}

            {/* ── CYBER & DIGITAL RISK ── */}
            {r.cyberRisk && (
                <Section title="Cyber & Digital Risk Posture">
                    <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-5">
                        <p className="text-sm text-slate-700 mb-4">{r.cyberRisk.summary}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {r.cyberRisk.topThreats?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-cyan-700 mb-2">Top Cyber Threats</h6>
                                    <ul className="space-y-1">
                                        {r.cyberRisk.topThreats.map((threat: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-red-500 mt-0.5">⚠</span>{threat}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {r.cyberRisk.recommendedActions?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-cyan-700 mb-2">Defensive Actions</h6>
                                    <ul className="space-y-1">
                                        {r.cyberRisk.recommendedActions.map((action: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-emerald-500 mt-0.5">✓</span>{action}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            )}

            {/* ── REGULATORY & COMPLIANCE POSTURE ── */}
            {r.regulatoryPosture && (
                <Section title="Regulatory & Compliance Posture">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                        <p className="text-sm text-slate-700 mb-4">{r.regulatoryPosture.summary}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {r.regulatoryPosture.keyGaps?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-amber-700 mb-2">Key Compliance Gaps</h6>
                                    <ul className="space-y-1">
                                        {r.regulatoryPosture.keyGaps.map((gap: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-amber-500 mt-0.5">▲</span>{gap}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {r.regulatoryPosture.recommendedActions?.length > 0 && (
                                <div>
                                    <h6 className="text-xs font-black uppercase tracking-widest text-amber-700 mb-2">Compliance Actions</h6>
                                    <ul className="space-y-1">
                                        {r.regulatoryPosture.recommendedActions.map((action: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                <span className="text-emerald-500 mt-0.5">✓</span>{action}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            )}

            {/* ── KEY VULNERABILITIES ── */}
            {result.keyVulnerabilities && result.keyVulnerabilities.length > 0 && (
                <Section title="Key Enterprise Vulnerabilities">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {result.keyVulnerabilities.map((vuln, i) => (
                            <div key={i} className="flex items-start gap-3 bg-white p-4 border rounded shadow-sm">
                                <div className="text-red-500 mt-1">⚠️</div>
                                <p className="text-sm text-slate-700">{vuln}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* ── STRATEGIC REMEDIATION PATHWAY ── */}
            {result.remediationActions && result.remediationActions.length > 0 && (
                <Section title="Strategic Remediation Pathway">
                    <div className="space-y-4">
                        {result.remediationActions.map((action, i) => (
                            <div key={i} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-blue-400 transition-all group">
                                <div className="flex-grow">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase ${
                                            action.priority === 'Critical' ? 'bg-red-600 text-white' :
                                            action.priority === 'High' ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'
                                        }`}>
                                            {action.priority} Priority
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{action.targetSuite}</span>
                                    </div>
                                    <h5 className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors uppercase tracking-tight">{action.title}</h5>
                                    <p className="text-sm text-slate-600 mt-1">{action.description}</p>
                                </div>
                                <button
                                    onClick={() => openRemediationContext(action)}
                                    className="flex-shrink-0 bg-slate-900 text-white px-6 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    Execute Remediation
                                </button>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {pendingAction && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-6 text-white shadow-2xl">
                        <div className="mb-5 flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-400">Remediation Context</p>
                                <h3 className="mt-1 text-xl font-black">{pendingAction.title}</h3>
                            </div>
                            <button
                                onClick={() => setPendingAction(null)}
                                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                                aria-label="Close remediation context"
                            >
                                ✕
                            </button>
                        </div>

                        <p className="mb-5 text-sm leading-relaxed text-slate-300">Choose whether this action applies enterprise-wide or targets one country or territory.</p>

                        <div className="mb-5 grid grid-cols-2 gap-3">
                            <button
                                onClick={() => { setScope('global'); setCountryError(''); }}
                                className={`rounded-xl border px-4 py-3 text-sm font-bold transition-colors ${scope === 'global' ? 'border-blue-500 bg-blue-600 text-white' : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'}`}
                            >
                                Global
                            </button>
                            <button
                                onClick={() => setScope('country')}
                                className={`rounded-xl border px-4 py-3 text-sm font-bold transition-colors ${scope === 'country' ? 'border-blue-500 bg-blue-600 text-white' : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'}`}
                            >
                                Country or Territory
                            </button>
                        </div>

                        {scope === 'country' && (
                            <div className="mb-5">
                                <label htmlFor="remediation-country" className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-300">Target Country or Territory</label>
                                <input
                                    id="remediation-country"
                                    list="remediation-country-options"
                                    value={targetCountry}
                                    onChange={event => { setTargetCountry(event.target.value); setCountryError(''); }}
                                    placeholder="Search countries and territories..."
                                    autoFocus
                                    className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                />
                                <datalist id="remediation-country-options">
                                    {COUNTRIES.map(country => <option key={country} value={country} />)}
                                </datalist>
                                {countryError && <p className="mt-2 text-xs font-semibold text-red-400">{countryError}</p>}
                            </div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setPendingAction(null)}
                                className="rounded-lg px-4 py-2 text-sm font-bold text-slate-300 hover:bg-slate-800"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeRemediation}
                                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-black text-white hover:bg-blue-500"
                            >
                                Continue Remediation
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


export default EnterpriseAuditResultDisplay;
